import React, { useState, useEffect, useMemo } from 'react';
import { fetchLeaderboard } from './actions/TileActions';
import { gameState } from './state/GameState';
import { getDailyModelId, getModelById } from './voxel/models';
import { publicShellPayload, shellVoxelsForSlice } from './voxel/slicePipeline';
import { getEnergyState, ENERGY_MAX } from './meta/progression';
import HologramViewport from '../components/HologramViewport';
import { IconKey, IconNodes, IconSquad, IconTools } from '../components/AtelierIcons';

const CRAFT_TITLES = [
    'Master Horologist',
    'Senior Carver',
    'Forge Adept',
    'Atelier Craftsman',
    'Studio Mason',
    'Apprentice Carver',
];

function guildOrderFromDate(dateStr) {
    let h = 0;
    for (let i = 0; i < dateStr.length; i++) h = (h * 31 + dateStr.charCodeAt(i)) >>> 0;
    return String(400 + (h % 500)).padStart(3, '0');
}

function msUntilUtcMidnight() {
    const now = new Date();
    const end = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1);
    return Math.max(0, end - now.getTime());
}

function formatRemain(ms) {
    const totalMin = Math.floor(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return `${h}h ${String(m).padStart(2, '0')}m`;
}

function masonHandle(entry) {
    if (entry.displayName) return entry.displayName;
    const id = String(entry.userId || 'mason');
    if (id.startsWith('dev_')) return id.replace(/^dev_/, 'mason_');
    return `mason_${id.slice(0, 8)}`;
}

function avatarTone(seed) {
    const hues = ['#e8772a', '#6faf7a', '#c4a484', '#ffb68d', '#94d69d', '#8a5a3a'];
    let h = 0;
    const s = String(seed || '');
    for (let i = 0; i < s.length; i++) h = (h + s.charCodeAt(i) * (i + 1)) % hues.length;
    return hues[h];
}

function IconChest(props) {
    return (
        <svg
            width="1em"
            height="1em"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            {...props}
        >
            <path d="M4 10h16v10H4z" />
            <path d="M4 10V7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3" />
            <path d="M12 10v10" />
            <path d="M10 14h4" />
        </svg>
    );
}

const Leaderboard = ({ onClose }) => {
    const [scores, setScores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState('SQUAD');
    const [remainLabel, setRemainLabel] = useState(() => formatRemain(msUntilUtcMidnight()));
    const [energy] = useState(() => getEnergyState());

    const date = useMemo(() => new Date().toISOString().split('T')[0], []);
    const orderNo = useMemo(() => guildOrderFromDate(date), [date]);
    const model = useMemo(
        () => getModelById(getDailyModelId(date, gameState.mysteryTier || 'atelier')),
        [date, gameState.mysteryTier]
    );
    const meId = gameState.currentUser?.id;

    const vaultStats = useMemo(() => {
        const tiles = Array.from(gameState.vault.getTiles());
        const total = tiles.length || 0;
        const solved = tiles.filter((t) => t.status === 'SOLVED').length;
        const pct = total ? Math.round((solved / total) * 100) : 0;
        const complete = total > 0 && solved >= total;
        return { tiles, total, solved, pct, complete };
    }, [scores, loading]);

    const hologram = useMemo(() => {
        if (!model) return null;
        // Never leak the full silhouette before the vault is complete.
        if (vaultStats.complete) {
            const payload = publicShellPayload(model, null);
            payload.voxels = (payload.voxels || []).map((v) => ({ ...v, revealed: true }));
            return payload;
        }
        const revealedKeys = new Set();
        for (const t of vaultStats.tiles) {
            if (t.status !== 'SOLVED') continue;
            const axis = t.data?.axis;
            const index = t.data?.index;
            if (axis == null || index == null) continue;
            shellVoxelsForSlice(model.volume, axis, index).forEach((v) => {
                revealedKeys.add(`${v.x},${v.y},${v.z}`);
            });
        }
        const payload = publicShellPayload(model, revealedKeys);
        payload.voxels = (payload.voxels || []).map((v) => ({
            ...v,
            revealed: revealedKeys.has(`${v.x},${v.y},${v.z}`),
        }));
        return payload;
    }, [model, vaultStats]);

    const masonCount = Math.max(scores.length, 1);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await fetchLeaderboard(tab === 'GLOBAL');
                setScores(data);
            } catch (err) {
                console.error(err);
                setError(err.message || 'Failed to load rankings.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [tab]);

    useEffect(() => {
        const tick = () => setRemainLabel(formatRemain(msUntilUtcMidnight()));
        tick();
        const id = setInterval(tick, 30000);
        return () => clearInterval(id);
    }, []);

    const mysteryTitle = vaultStats.complete
        ? model?.name || "Today's Mystery"
        : "TODAY'S MYSTERY";
    const mysteryBlurb = vaultStats.complete
        ? `${String(model?.category || 'Archive').toUpperCase()} · restored`
        : `${String(model?.category || 'mystery').toUpperCase()} · name locked until reveal`;

    return (
        <div className="atelier-overlay atelier-squad" role="dialog" aria-label="Squad Ledger">
            <div className="atelier-overlay-shell">
                <header className="atelier-overlay-top">
                    <div className="atelier-brand atelier-brand--compact">
                        <span className="atelier-brand-mark" aria-hidden>
                            <IconTools />
                        </span>
                        <div>
                            <div className="atelier-brand-name">Voxel Atelier</div>
                            <div className="atelier-brand-sub atelier-brand-sub--ember">
                                GUILD ORDER #{orderNo}
                            </div>
                        </div>
                    </div>
                    <div className="atelier-keys atelier-keys--compact" title="Energy keys">
                        <span className="atelier-keys-icon" aria-hidden>
                            <IconKey />
                        </span>
                        <span>
                            {energy.keys}/{ENERGY_MAX} Keys
                        </span>
                    </div>
                </header>

                <section className="atelier-active-card" aria-label="Active mystery">
                    <div className="atelier-active-main">
                        <div className="atelier-active-thumb">
                            {hologram && vaultStats.solved > 0 ? (
                                <HologramViewport
                                    size={hologram.size}
                                    voxels={hologram.voxels}
                                    palette={hologram.palette}
                                    grayscale={!vaultStats.complete}
                                    colorBlend={vaultStats.complete ? 1 : 0}
                                    autoRotate={false}
                                    presentation="trophy"
                                    height={88}
                                />
                            ) : (
                                <div className="atelier-active-thumb-fallback">?</div>
                            )}
                        </div>
                        <div className="atelier-active-copy">
                            <div className="atelier-active-badges">
                                <span
                                    className={`atelier-status-pill${
                                        vaultStats.complete ? ' is-solved' : ' is-claimed'
                                    }`}
                                >
                                    {vaultStats.complete ? 'RESTORED' : 'IN PROGRESS'}
                                </span>
                                <span className="atelier-active-timer">{remainLabel} REMAINING</span>
                            </div>
                            <h2 className="atelier-active-title">{mysteryTitle}</h2>
                            <p className="atelier-active-desc">{mysteryBlurb}</p>
                        </div>
                    </div>
                    <div className="atelier-active-progress">
                        <div className="atelier-active-progress-labels">
                            <span>
                                <IconNodes className="atelier-inline-ico" />
                                {masonCount} {masonCount === 1 ? 'Mason' : 'Masons'} Collaborating
                            </span>
                            <span>
                                {vaultStats.solved}/{vaultStats.total || '—'} Slices ({vaultStats.pct}
                                %)
                            </span>
                        </div>
                        <div className="atelier-active-progress-track">
                            <div
                                className="atelier-active-progress-fill"
                                style={{ width: `${vaultStats.pct}%` }}
                            />
                        </div>
                    </div>
                </section>

                <h1 className="atelier-overlay-title">Squad Ledger</h1>
                <p className="atelier-overlay-sub">
                    Today&apos;s contribution ranks · resets with the vault
                </p>

                <div className="atelier-seg" role="tablist" aria-label="Leaderboard scope">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === 'SQUAD'}
                        className={`atelier-seg-btn${tab === 'SQUAD' ? ' is-active' : ''}`}
                        onClick={() => setTab('SQUAD')}
                    >
                        Squad
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === 'GLOBAL'}
                        className={`atelier-seg-btn${tab === 'GLOBAL' ? ' is-active' : ''}`}
                        onClick={() => setTab('GLOBAL')}
                    >
                        Global
                    </button>
                </div>

                <div className="atelier-ledger-head" aria-hidden>
                    <span>RNK</span>
                    <span>CRAFTSMAN</span>
                    <span>CONTRIBUTIONS</span>
                </div>

                <div className="atelier-ledger">
                    {loading && (
                        <div className="atelier-ledger-status">Unrolling the ledger…</div>
                    )}

                    {error && <div className="atelier-ledger-status is-error">{error}</div>}

                    {!loading && !error && scores.length === 0 && (
                        <div className="atelier-empty-panel">
                            <div className="atelier-empty-glyph">⌘</div>
                            <h2>No solves yet today</h2>
                            <p>Be the first mason to log a reconstructed slice.</p>
                        </div>
                    )}

                    {!loading &&
                        !error &&
                        scores.map((entry, index) => {
                            const handle = masonHandle(entry);
                            const isYou = meId && entry.userId === meId;
                            const title = CRAFT_TITLES[Math.min(index, CRAFT_TITLES.length - 1)];
                            const solves = Number(entry.score) || 0;
                            const xp = solves * 30;
                            const initial = (handle.replace(/^mason_/, '')[0] || 'M').toUpperCase();
                            return (
                                <div
                                    key={entry.userId || index}
                                    className={`atelier-ledger-row${
                                        index < 3 ? ' is-podium' : ''
                                    }${isYou ? ' is-you' : ''}`}
                                >
                                    <div
                                        className={`atelier-rank${
                                            index === 0
                                                ? ' is-gold'
                                                : index === 1
                                                  ? ' is-silver'
                                                  : index === 2
                                                    ? ' is-bronze'
                                                    : ''
                                        }`}
                                    >
                                        {String(index + 1).padStart(2, '0')}
                                    </div>
                                    <div
                                        className="atelier-ledger-avatar"
                                        style={{ background: avatarTone(entry.userId || handle) }}
                                        aria-hidden
                                    >
                                        {initial}
                                    </div>
                                    <div className="atelier-ledger-who">
                                        <div className="atelier-ledger-name">
                                            {handle}
                                            {isYou && (
                                                <span className="atelier-you-tag">YOU</span>
                                            )}
                                        </div>
                                        <div className="atelier-ledger-tag">{title}</div>
                                    </div>
                                    <div className="atelier-ledger-contrib">
                                        <span className="atelier-solves-pill">
                                            {solves} SOLVES
                                        </span>
                                        <span className="atelier-xp">{xp} XP</span>
                                    </div>
                                </div>
                            );
                        })}
                </div>

                <aside className="atelier-perk-card">
                    <div className="atelier-perk-top">
                        <span className="atelier-perk-icon" aria-hidden>
                            <IconChest />
                        </span>
                        <div className="atelier-perk-copy">
                            <div className="atelier-perk-title-row">
                                <strong>Guild Chest Tier 2 Unlocked!</strong>
                                <span className="atelier-status-pill is-claimed">PERK ACTIVE</span>
                            </div>
                            <p>
                                +1 Atelier Key awarded to all squad masons upon 100% Mystery Reveal.
                                Keep contributing slices!
                            </p>
                        </div>
                    </div>
                </aside>

                <p className="atelier-ledger-foot">
                    Ranks reset daily with the mystery · Archive #{orderNo} closes in {remainLabel}
                </p>

                <button type="button" className="atelier-chip-btn atelier-squad-done" onClick={onClose}>
                    <IconSquad className="atelier-inline-ico" />
                    Done · return to Hub
                </button>
            </div>
        </div>
    );
};

export default Leaderboard;
